import {
  EmbeddedMessage,
  MESSAGE_MEDIUMS_WITH_DESTINATION,
  MessageMedium,
} from 'constants/messageMediums';
import isNil from 'lodash/isNil';
import {
  AttributionMode,
  AudienceList,
  Campaign,
  CampaignState,
  CampaignType,
  Conversion,
  ConversionEvent,
  DataScienceOptimization,
  Experiment,
  ExperimentVariant,
  RateLimitUnit,
} from 'modules/domains/campaign';
import { ExperimentType } from 'modules/domains/campaign/types';
import { ExperimentState } from 'modules/graphql';
import { Moment } from 'moment';
import { P, match } from 'ts-pattern';
import { trim24HourClockTime } from 'utils/time';
import { Nullable, Nullish } from 'utils/tsHelpers';
import {
  DEFAULT_CONVERSIONS_PERIOD_HOUR,
  DEFAULT_QUIET_HOURS_END,
  DEFAULT_QUIET_HOURS_START,
  DEFAULT_RATE_LIMIT_PER_MINUTE,
  DEFAULT_STO_COMPLETION_TIME,
} from '../lib/constants';
import { ExpectedSendSize } from '../lib/hooks';
import { GraphQLCampaign } from '../lib/types';
import { getIsTriggeredCampaignType, getIsWhatsAppCampaign, pluralizeWord } from '../lib/utils';
import {
  getHasCampaignRateLimit,
  getHasConversions,
  getHasExperiments,
  getHasList,
  getHasPriorityLevel,
  getHasQuietHours,
  getIsEligibilityCampaign,
} from './components/utils';
import { PRIORITY_LEVEL_VALUES } from './constants';
import { CampaignEditorFormState, PriorityLevelDisplay } from './types';

// defaults for conversions
const getConversions = (
  conversions: Conversion[],
  defaultValues: { events: string[]; mode: AttributionMode; periodHours: string },
) => {
  const { events, mode, periodHours } = defaultValues;

  return {
    useConversions: getHasConversions(conversions),
    events:
      conversions?.reduce(
        (acc: string[], curr: Conversion) => [
          ...acc,
          ...curr?.events.map((event: ConversionEvent) => event.name),
        ],
        [],
      ) || events,
    mode: (conversions?.[0]?.events?.[0]?.attributionMode?.name || mode) as AttributionMode,
    periodHours:
      conversions?.[0]?.events?.[0]?.attributionMode?.periodHours?.toString() || periodHours,
  };
};

const getExperiments = (experiments: Experiment[]) => ({
  useExperiments: getHasExperiments(experiments),
  experiments,
});

/**
 * Returns whether a given campaign has Send Time Optimization (STO) enabled
 * @see https://github.com/Iterable/Iterable/blob/c5aa37f435642d1964be27e6630303a1c53ebc4f/platform/app/com/iterable/models/Campaign.scala#L194
 */
export const getHasSendTimeOptimization = (campaign: GraphQLCampaign) => {
  const { schedulerMsgParams, dataScienceOptimizations } = campaign || {};

  return (
    !!dataScienceOptimizations?.includes(DataScienceOptimization.SendTimeOptimization) ||
    !!schedulerMsgParams?.automaticSendTime
  );
};

/**
 * Returns whether a given campaign has an attached Send Time Optimization (STO) Experiment
 */
export const getHasSendTimeOptimizationExperiment = (campaign: GraphQLCampaign) => {
  return !!campaign?.experiments?.find(
    (experiment) => experiment?.experimentType === ExperimentType.SendTimeOptimization,
  );
};

const getIsActivatedTriggeredOrScheduledBlastCampaign = (campaign: Campaign) => {
  // activated triggered campaign
  if (
    getIsTriggeredCampaignType(campaign.campaignTypeDefinition) &&
    campaign.campaignState === CampaignState.Running
  )
    return true;
  // scheduled campaign
  if (campaign.campaignState === CampaignState.Scheduled) return true;
  return false;
};

export const canAddExperiment = (campaign: Campaign, experiment: Nullable<Experiment>) => {
  const isActivatedOrScheduledCampaign = getIsActivatedTriggeredOrScheduledBlastCampaign(campaign);
  const isRunningOrDraftExperiment = [ExperimentState.RUNNING, ExperimentState.DRAFT].includes(
    experiment?.state as ExperimentState,
  );
  // For a scheduled campaign or an activated trigger campaign
  // Do not display the “Create Experiment” option in the header if there is already an experiment attached to the campaign that is in draft or running status
  // Also do not display the option for Eligibility campaigns since they are not supported
  if (
    (isActivatedOrScheduledCampaign && isRunningOrDraftExperiment) ||
    getIsEligibilityCampaign(campaign.campaignTypeDefinition) ||
    getIsWhatsAppCampaign(campaign.messageMedium)
  ) {
    return false;
  }
  // Show the "Create Experiment" option only when there is no attached experiment
  // or when the attached experiment is completed,
  // or if it's a completed trigger campaign. Since, trigger campaign can be reactivated later.
  return (
    !experiment ||
    experiment?.state === ExperimentState.FINISHED ||
    (getIsTriggeredCampaignType(campaign.campaignTypeDefinition) &&
      campaign.campaignState === CampaignState.Finished)
  );
};

/**
 * Returns audience "sub-CampaignEditorFormState"
 */
const getAudienceLists = (sendLists: AudienceList[], suppressionLists: AudienceList[]) => ({
  useSuppressionLists: getHasList(suppressionLists),
  useSendLists: getHasList(sendLists),
  sendLists,
  suppressionLists,
});

/**
 * Returns initial values for the campaign editor form based on saved campaign data.
 */
export const getCampaignEditorFormInitialValues = (
  campaign: GraphQLCampaign,
  isJourneyGoalsEnabled: boolean,
): CampaignEditorFormState => {
  const {
    customQuietHours,
    schedulerMsgParams,
    customRateLimit,
    conversions,
    experiments,
    priorityLevel,
    campaignAlertsEnabled,
    lists,
    suppressionLists,
    ignoreFrequencyCap,
  } = campaign;

  const hasSendTimeOptimization = getHasSendTimeOptimization(campaign);
  const hasSendTimeOptimizationExperiment = getHasSendTimeOptimizationExperiment(campaign);

  return {
    ignoreFrequencyCap: Boolean(ignoreFrequencyCap),
    useQuietHours: getHasQuietHours(customQuietHours),
    customQuietHours: {
      startTime: customQuietHours?.startTime
        ? trim24HourClockTime(customQuietHours.startTime)
        : DEFAULT_QUIET_HOURS_START,
      endTime: customQuietHours?.endTime
        ? trim24HourClockTime(customQuietHours.endTime)
        : DEFAULT_QUIET_HOURS_END,
      sendSkip: !!customQuietHours?.sendSkip,
    },
    /**
     * Send Time Optimization (STO) should be ENABLED initially for the following cases:
     * 1. The Campaign has STO enabled
     * 2. The Campaign has an attached STO Experiment
     *
     * @see https://iterable.atlassian.net/browse/CAMP-1055?focusedCommentId=254822
     */
    useSendTimeOptimization: hasSendTimeOptimization || hasSendTimeOptimizationExperiment,
    stoCompletionTime: (schedulerMsgParams?.windowHours || DEFAULT_STO_COMPLETION_TIME)?.toString(),
    campaignAlertsEnabled: Boolean(campaignAlertsEnabled),

    useRateLimit: getHasCampaignRateLimit(customRateLimit),
    rateLimit: {
      rateLimitValue: (
        customRateLimit?.rateLimitPerMin || DEFAULT_RATE_LIMIT_PER_MINUTE
      ).toString(),
      /**
       * This value is not currently saved-able in the backend and always defaults to minute.
       * @see: https://iterable.slack.com/archives/C03CD2W2XJA/p1657200684046669
       */
      rateLimitUnit: RateLimitUnit.Minute,
    },
    conversions: getConversions(conversions, {
      events: [],
      mode: isJourneyGoalsEnabled ? AttributionMode.LastMessaged : AttributionMode.ExplicitlySet,
      periodHours: DEFAULT_CONVERSIONS_PERIOD_HOUR.toString(),
    }),
    experiments: getExperiments(experiments),
    audienceLists: getAudienceLists(lists, suppressionLists),
    priorityLevel: getHasPriorityLevel(priorityLevel)
      ? priorityLevel
      : PRIORITY_LEVEL_VALUES[PriorityLevelDisplay.Medium],
  };
};

/**
 * Returns whether STO is enabled
 */
export const getIsSTOEnabled = (dataScienceOptimizations: DataScienceOptimization[] = []) =>
  !!dataScienceOptimizations?.includes(DataScienceOptimization.SendTimeOptimization);

/**
 * Rounds a moment time to the nearest given interval in minutes
 */
export const roundMomentToNearestInterval = (interval: number, date: Moment) => {
  const roundedMinutes = Math.round(date.clone().minute() / interval) * interval;
  return date.clone().minute(roundedMinutes).second(0);
};

/**
 * Destination field is only for in-app, push, flex
 * We will update this to include embedded when it's ready
 */
export const getIsMediumWithDestination = (messageMedium: MessageMedium) =>
  MESSAGE_MEDIUMS_WITH_DESTINATION.includes(messageMedium);

/**
 * Returns whether a campaign is in a pre-scheduled/activated state
 */
export const getIsNotLaunchedCampaign = ({ campaignState, startAt }: GraphQLCampaign) =>
  [CampaignState.Draft, CampaignState.Ready].includes(campaignState) || !startAt;

/**
 * NOTE: this is used in many places, so be mindful when changing it.
 */
export const getRecipientsDisplayText = (sendSize: ExpectedSendSize, isEstimated = false) =>
  `${sendSize?.toLocaleString()} ${pluralizeWord('recipient', sendSize)}${
    isEstimated ? ' (est.)' : ''
  }`;

/**
 * Returns recipients count text or default recipients count text when `sendSize` is not available
 */
export const getSendSizeContent = (sendSize: ExpectedSendSize, isEstimated?: boolean) =>
  isNil(sendSize)
    ? getRecipientsDisplayText(0, false)
    : getRecipientsDisplayText(sendSize, isEstimated);

/**
 * @param campaignState
 * @returns campaign state that campaign with given current state is
 * eligible to be transitioned to
 */
export const getCampaignStateForTransition = (
  campaignState: CampaignState,
): CampaignState | undefined => {
  switch (campaignState) {
    case CampaignState.Running:
      return CampaignState.Finished;
    case CampaignState.Finished:
    case CampaignState.Aborted:
      return CampaignState.Running;
    default:
      // not technically default but we need some kind of state fallback
      return undefined;
  }
};

/**
 * Tries to get an error message from a REST error response.
 * If error message is not found, an empty string is returned
 */
export const maybeRestErrorMessage = (error: any): string => {
  const errMessage = error?.response?.data?.message || error?.response?.data || error?.message;
  return match(errMessage)
    .with(P.string, () => errMessage)
    .otherwise(() => '');
};

/**
 * Returns toast message for successfully scheduled campaigns
 */
export const getSuccessfullyScheduledCampaignMessage = (
  name?: string,
  isScheduledCampaign?: boolean,
) => `${name} ${isScheduledCampaign ? 'rescheduled' : 'scheduled'} successfully.`;

/**
 * Returns toast message for unsuccessfully scheduled campaigns
 */
export const getErrorSchedulingCampaignMessage = (error?: any) => {
  const errorResponseMessage = maybeRestErrorMessage(error);
  const defaultErrorMessage = 'Error scheduling campaign: something went wrong.';

  return errorResponseMessage || defaultErrorMessage;
};

/**
 * Returns send time experiments for `experiments`
 */
export const getSendTimeExperimentVariants = (
  experiments: GraphQLCampaign['experiments'],
): Nullish<ExperimentVariant[]> =>
  experiments
    ?.filter(({ experimentType }) => experimentType === ExperimentType.SendTime)?.[0]
    ?.variations?.filter(({ sendTime }) => !!sendTime);

/**
 * Returns whether a campaign has STO and a sent time experiment attached
 */
export const getHasStoAndSendTimeExperiment = (campaign: GraphQLCampaign) => {
  const sendTimeVariants = getSendTimeExperimentVariants(campaign?.experiments);
  const hasSto = getHasSendTimeOptimization(campaign);

  return (sendTimeVariants?.length ?? 0) > 0 && hasSto;
};

/**
 * Returns whether a campaign has delivery settings that affects a campaign's send time
 */
export const getHasDeliverySettings = (campaign: GraphQLCampaign) => {
  return Boolean(
    campaign?.customQuietHours ||
      campaign?.customRateLimit?.useRateLimit ||
      getHasSendTimeOptimization(campaign),
  );
};

type GetShouldShowPerformanceAlertsArgs = {
  campaignType: CampaignType;
  isCampaignPerformanceAlertsEnabled: boolean;
  isJourneysCampaignPerformanceAlertsEnabled: boolean;
  isJourneyAssociated?: boolean;
  isWhatsAppCampaign?: boolean;
};

/**
 * Returns whether Campaigns Performance Alerts feature should be displayed for a campaign
 */
export const getShouldShowPerformanceAlerts = ({
  campaignType,
  isCampaignPerformanceAlertsEnabled,
  isJourneysCampaignPerformanceAlertsEnabled,
  isJourneyAssociated = false,
  isWhatsAppCampaign = false,
}: GetShouldShowPerformanceAlertsArgs) => {
  if (isWhatsAppCampaign) {
    return false;
  } else if (isJourneyAssociated) {
    return isCampaignPerformanceAlertsEnabled && isJourneysCampaignPerformanceAlertsEnabled;
  }

  switch (campaignType) {
    /**
     * This case technically won't be accessed currently as `isJourneyAssociated`
     * above should catch campaigns from both draft and published journeys
     * ...but one day if/when we properly update the campaign types categorization
     * for campaigns in draft journeys we can switch over to this exclusively.
     */
    case CampaignType.WorkflowTriggered:
      return isCampaignPerformanceAlertsEnabled && isJourneysCampaignPerformanceAlertsEnabled;

    default:
      return isCampaignPerformanceAlertsEnabled;
  }
};

/**
 * Returns if the campaign is a non published Eligibility campaign based on type and channel
 */
export const getIsNonPublishedEligibilityCampaign = (campaign: GraphQLCampaign) =>
  campaign?.isJourneyAssociated &&
  campaign.messageMedium === EmbeddedMessage &&
  campaign.campaignTypeDefinition === CampaignType.Eligibility;
